<template>
  <div class="password-overlay">
    <div class="password-container">
      <div class="password-content">
        <div class="logo-section">
          <h1 class="site-title">
    
          <Logo/>
          
          </h1>
          
        </div>

        <form @submit.prevent="handleSubmit" class="password-form">
          <div class="input-group">
            <label for="password" class="password-label">
              {{ isGlobalPassword ? 'Website Password' : 'Page Password' }}
            </label>
            <input
              id="password"
              v-model="passwordInput"
              type="password"
              class="password-input"
              :placeholder="isGlobalPassword ? 'Enter website password' : 'Enter page password'"
              autocomplete="current-password"
              required
            />
          </div>

          <button type="submit" class="password-submit" :disabled="!passwordInput.trim()">
            Enter
          </button>

          <p v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </p>
        </form>

       
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { PasswordProtection } from '../stores/strapi/types'
import Logo from './Logo.vue'

interface Props {
  isGlobalPassword?: boolean
  passwordConfig?: PasswordProtection | PasswordProtection[] | null
  description?: string
  showHint?: boolean
}

interface Emits {
  (e: 'passwordCorrect'): void
}

const props = withDefaults(defineProps<Props>(), {
  isGlobalPassword: false,
  description: 'This content is password protected.',
  showHint: true
})

const emit = defineEmits<Emits>()

const passwordInput = ref('')
const errorMessage = ref('')

const expectedPasswords = computed(() => {
  if (!props.passwordConfig) {
    return ['cross-disciplinary'] // fallback
  }
  if (Array.isArray(props.passwordConfig)) {
    return props.passwordConfig
      .filter(p => p.passwordProtected)
      .map(p => p.password)
  }
  return props.passwordConfig.passwordProtected
    ? [props.passwordConfig.password]
    : []
})

const handleSubmit = () => {
  const input = passwordInput.value.trim()
  if (!input) {
    errorMessage.value = 'Please enter a password'
    return
  }
  if (expectedPasswords.value.includes(input)) {
    errorMessage.value = ''
    emit('passwordCorrect')
  } else {
    errorMessage.value = 'Incorrect password'
    passwordInput.value = ''
  }
}
</script>

<style scoped lang="scss">
.password-overlay {
  position: fixed;
  inset: 0;
  background: #eee;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-container {
  width: 90%;
  max-width: 320px;
}



.logo-section {
  margin-bottom: 1rem;
  text-align: center;
}

.site-title,
.site-description {
  margin: 0;
  color: var(--theme-text, #000);
}

.password-form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 50px;
}

.password-label {
  font-weight: 600;
  color: var(--theme-text, #000);
  padding-bottom: 10px;
  font-weight: 300;
  display: block;

}

.password-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--theme-border, #ccc);
  border-radius: 4px;
  background: var(--theme-input-bg, #fff);
}

.password-submit {
  padding: 0.5rem;
  border: none;
  background: var(--theme-accent, #007bff);
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
}

.password-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message {
  color: var(--theme-error, #ef4444);
  font-size: 0.875rem;
}

.password-hint {
  margin-top: 1rem;
  text-align: center;
}

.hint-text {
  color: var(--theme-text-secondary, #666);
  font-size: 0.85rem;
}
</style>
